import { pxToRem } from '@/Client/Styles/pxToRem';
import { formatDate, toDate } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useFormikContext } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { CalendarIcon } from '../../Icons';
import { TYPOGRAPHY2 } from '../../Styles';
import { PositionedContainer } from '../PositionedContainer';
import { LabeledInputSize, LabeledInputStyle } from './LabeledInputCommonTypes';
import { LabeledInputPresenter } from './LabeledInputPresenter';

const LABELED_CALENDAR_INPUT_STYLE = css`
  label: LABELED_CALENDAR_INPUT_STYLE;
`;

const VALUE_TYPOGRAPHY_SIZES = {
  tiny: css`
    label: VALUE_TYPOGRAPHY_SIZES_TINY;
    ${TYPOGRAPHY2.xSmall}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
  `,
  small: css`
    label: VALUE_TYPOGRAPHY_SIZES_SMALL;
    ${TYPOGRAPHY2.xSmall}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
  `,
  tightSmall: css`
    label: VALUE_TYPOGRAPHY_SIZES_SMALL;
    ${TYPOGRAPHY2.xSmall}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
  `,
  medium: css`
    label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
    ${TYPOGRAPHY2.medium}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.medium)} !important;
  `,
  tightMedium: css`
    label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
    ${TYPOGRAPHY2.medium}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.medium)} !important;
  `,
  large: css`
    label: VALUE_TYPOGRAPHY_SIZES_LARGE;
    ${TYPOGRAPHY2.large}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.large)} !important;
  `,
};

const PICKLIST_TEXT_STYLE = css`
  label: PICKLIST_TEXT_STYLE;
  cursor: pointer;
  flex: 1;
  display: flex;
  align-items: center;
  color: var(--text);
`;

const PICKLIST_TEXT_PLACEHOLDER_STYLE = css`
  label: PICKLIST_TEXT_PLACEHOLDER_STYLE;
  color: var(--text2);
  font-style: italic;
`;

const CALENDAR_DROPDOWN_STYLE = css`
  label: CALENDAR_DROPDOWN_STYLE;
  background: var(--background);
  border: 1px solid var(--border2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const CALENDAR_ICON_STYLE = css`
  label: CALENDAR_ICON_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text);
`;

export type LabeledCalendarInputProps = {
  title?: string;
  label?: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  style?: LabeledInputStyle;
  size?: LabeledInputSize;
  width?: string;
  onChange?: (value: string) => void;
  onUpdate?: (value: string) => void;
  backgroundColor?: string;
  delay?: number;
};

export const LabeledCalendarInput = ({
  title,
  label,
  value: dateValue,
  placeholder = 'Select date',
  disabled = false,
  style = 'normal',
  size = 'medium',
  width = '100%',
  delay = 500,
  onChange,
  onUpdate,
  backgroundColor,
}: LabeledCalendarInputProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedUpdate = useDebouncedCallback(
    (updatedValue: string) => onUpdate?.(updatedValue),
    delay,
  );

  const toggleCalendar = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setIsCalendarOpen(!isCalendarOpen);
  };

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      const formattedDate = toDate(newDate).toISOString();
      onChange?.(formattedDate);
      debouncedUpdate(formattedDate);
      debouncedUpdate.flush();
      setIsCalendarOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  const displayValue = dateValue ? formatDate(dateValue, 'MMM d, yyyy') : placeholder;
  const dateObjectValue = dateValue ? toDate(dateValue) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div ref={containerRef} css={LABELED_CALENDAR_INPUT_STYLE} style={{ width, backgroundColor }}>
        <LabeledInputPresenter
          width={width}
          style={style}
          size={size}
          backgroundColor={backgroundColor}
          onClick={disabled ? () => {} : toggleCalendar}
          title={title || ''}
          isFocused={isCalendarOpen}
          label={label}
          disabled={disabled}
        >
          <div
            css={[
              PICKLIST_TEXT_STYLE,
              VALUE_TYPOGRAPHY_SIZES[size],
              !dateValue && PICKLIST_TEXT_PLACEHOLDER_STYLE,
            ]}
          >
            {displayValue}
          </div>
          <div css={CALENDAR_ICON_STYLE} onClick={disabled ? undefined : toggleCalendar}>
            <CalendarIcon size={20} />
          </div>
        </LabeledInputPresenter>
        {isCalendarOpen
          && (
            <PositionedContainer targetRef={containerRef as React.RefObject<HTMLElement | null>} placement='bottom' offsetY={-1} offsetX={-1}>
              <div css={CALENDAR_DROPDOWN_STYLE}>
                <DateCalendar
                  value={dateObjectValue}
                  onChange={handleDateChange}
                />
              </div>
            </PositionedContainer>
          )}
      </div>
    </LocalizationProvider>
  );
};

type FormikLabeledCalendarInputProps = Omit<LabeledCalendarInputProps, 'value' | 'onChange' | 'onUpdate'> & {
  name: string;
};

export const FormikLabeledCalendarInput = (props: FormikLabeledCalendarInputProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  return (
    <LabeledCalendarInput
      {...props}
      value={values[props.name] || ''}
      onChange={value => setFieldValue(props.name, value)}
    />
  );
};
